import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useSelector } from 'umi';
import { Text } from 'tea-component';
import Permission from '@/pages/dashboard/components/NoPermission';
import GuideTableau from '@/pages/dashboard/components/UserGuide/Tableau';
import Loading from '@/pages/dashboard/components/Loadings';
import { getDashboardType, checkReportIdWithName, getPathFromDashboard } from '@/pages/dashboard/utils/utils';
import { getTicket } from '@/pages/dashboard/services/dashboard';
import {
  PAGE_TYPE_TABLEAU,
  PAGE_TYPE_WORKBOOK,
  PAGE_TYPE_URL,
  LOG_SYSTEM_TYPE,
} from '@/pages/dashboard/_config/constant';
import { IS_WOA } from '@/pages/home/_config/config';
import { buriedPoint } from '@/pages/home/utils/buriedPoint';
interface ReportInfoType {
  loading: boolean;
  iframeUrl: string;
  noReport: boolean;
}

const INIT_REPORT_INFO:ReportInfoType = {
  iframeUrl: '',
  noReport: false,
  loading: false
};
export default () => {
  const [reportInfo, setReportInfo] = useState<ReportInfoType>({
    loading: false,
    iframeUrl: '',
    noReport: false
  });
  const dashboardRef = useRef<any>(null);
  const vizRef = useRef<any>(null);
  const { reportId } = useParams() as any;
  const { currentMenu, currentGame } = useSelector(state => {
    // @ts-ignore
    const { currentMenu, currentGame } = state.dashboard;
    return {
      currentMenu,
      currentGame
    };
  });

  const setState = (params = {}) => {
    setReportInfo({
      ...INIT_REPORT_INFO,
      ...params
    });
  };

  // iframe 处理
  const openIframeReport = () => {
    if (!reportId ||!currentGame ) {
      return;
    }
    if (!currentMenu || getDashboardType(currentMenu) !== PAGE_TYPE_TABLEAU || !checkReportIdWithName(reportId, currentMenu?.name_en)) {
      return;
    }
    setState({
      loading: true
    });
    dashboardRef.current = currentMenu;
    const {page_type: pageType, url, urls, workbook_url: workbookUrl} = currentMenu;
    // 如果没有此参数 则都赋值为空
    const contentUrl = pageType === PAGE_TYPE_WORKBOOK ? workbookUrl :
      pageType === PAGE_TYPE_URL ? url : (workbookUrl || url);
    if (urls) {
      setState({
        iframeUrl: urls
      });
      return;
    }
    if (!contentUrl) {
      setState({
        noReport: true
      });
      return;
    }
    const { game_code: gameCode, studio } = currentGame;
    const params = {
      game_code: gameCode,
      user_id: '101',
      studio_code: studio,
      content_url: contentUrl
    };
    getTicket(params).then((data) => {
      //  同步老版本的逻辑 加了&& data?.full_url_inner 的判断条件
      const iframe = (IS_WOA && data?.full_url_inner) ? data?.full_url_inner: (data?.full_url || '');
      if (dashboardRef.current?.id !== currentMenu?.id) {
        return;
      }
      if (iframe) {
        setState({
          iframeUrl: iframe
        });
        // 打点
        const { id = '', name_en: nameEn } = currentMenu || {};
        const logObj = {
          pageId: id,
          menuId: id,
          pageName: nameEn,
          extInfo: nameEn,
          pagePath: getPathFromDashboard(currentMenu),
          uid: gameCode,
          typeId: LOG_SYSTEM_TYPE,
        };
        buriedPoint(logObj);
      } else {
        setState({
          noReport: true
        });
      }
    }, () => {
      if (dashboardRef.current?.id !== currentMenu?.id) {
        return;
      }
      setState({
        noReport: true
      });
    }).catch(e=>{
      console.log(e);
    });
  };

  useEffect(() => {
    openIframeReport();
  }, [reportId, currentMenu]);

  const errorInfo = useMemo(() => {
    // 路径中无reportid
    if (!reportId) {
      return <Permission type="error" textCN="未获取到报表信息" />;
    }
    // 无currentMenu，或者currentMenu不是tableau，或者currentMenu跟路径中的reportId不匹配，说明currentMenu 未更新，等待currentMenu更新，此时执行loading等待
    // if (!currentMenu || getDashboardType(currentMenu) !== PAGE_TYPE_TABLEAU || getTableauReportId(currentMenu) !== reportId || reportInfo.loading) {
    if (!currentMenu || getDashboardType(currentMenu) !== PAGE_TYPE_TABLEAU ||reportInfo.loading || !checkReportIdWithName(reportId, currentMenu?.name_en)) {
      return <Loading />;
    }
    // 无报表
    if (reportInfo.noReport) {
      return <div style={{
        width: '100%',
        height: '100%',
        background: '#eee',
      }}>
        <Text style={{
          position: 'relative',
          top: '50%',
          display: 'block',
          textAlign: 'center',
          fontSize: 14,
          color: '#999'
        }}>This function is not open yet. Please look forward to it.</Text>
      </div>;
    }
    return null;
  }, [currentMenu, reportId, reportInfo]);

  const { iframeUrl } = reportInfo || {};

  function onTabSwitch(event: any) {
    const viz = event.getViz();
    const workbook = viz.getWorkbook();
    const sheet = workbook.getActiveSheet();
    location.hash = sheet.getName();
  };

  const loadTableau = async (iframeUrl: string) => {
    if (!iframeUrl) {
      return;
    }
    const $div = document.querySelector('.dtb-db-iframe-wrap') as HTMLDivElement;
    if (!$div) {
      return;
    }
    await import('@/pages/home/plugin/tableau-2.9.2.min.js');
    // eslint-disable-next-line prefer-const
    const options = {     
      with: '100%',
      height: '100%',
      device: 'desktop',
      onFirstInteractive: () => {
        try {
          const sheetName = decodeURIComponent(location.hash.replace('#', ''));
          vizRef.current.getWorkbook().activateSheetAsync(sheetName);
        } catch (e) {}
        // 添加class，保留之前的class，用于机器人加载tableau时，元素判定
        const $iframe = document.querySelector('.dtb-db-iframe-wrap iframe');
        const className = 'dtb-db-iframe';
        if ($iframe && !$iframe.classList.contains(className)) {
          $iframe.classList.add(className);
        }
        vizRef.current.addEventListener((window as any).tableau.TableauEventName.TAB_SWITCH, onTabSwitch);
      }
    };
    if (vizRef.current) { // If a viz object exists, delete it.
      vizRef.current?.dispose?.();
    }
    vizRef.current = new (window as any).tableau.Viz($div, iframeUrl, options);
  };

  useEffect(() => {
    vizRef.current = null;
    loadTableau(iframeUrl);

    const handleHashChange = () => {
      // 从url中获取当前sheet的名字
      const sheetName = decodeURIComponent(location.hash.replace('#', ''));
      if (!sheetName || !iframeUrl || !vizRef.current) {
        return;
      }
      try {
        const workbook = vizRef.current?.getWorkbook?.();
        if (!workbook) {
          return;
        }
        const sheet = workbook?.getActiveSheet?.();

        // 如果当前sheet和url中的sheet不一致，则激活url中的sheet
        if (sheet?.getName?.() !== sheetName) {
          workbook.activateSheetAsync(sheetName);
        }
      } catch (e) {}
    }

    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    }
  }, [iframeUrl]);

  return (
    <>
      {
        errorInfo || <div className='dtb-db-iframe-wrap iframe-wrap-padding'>
          {/* <iframe
            title="tableau"
            className='dtb-db-iframe'
            // 确保iframeUrl不为undefined,避免出现PROMISE_ERROR: Error.message: XXXXXXXX 报错
            src={iframeUrl || ''}
          /> */}
        </div>
      }
      <GuideTableau/>
    </>
  );
};
